import { Modal, Flex, Result, Button, Spin } from "antd";
import { Document, Page, pdfjs } from "react-pdf";
import { ZoomInOutlined, ZoomOutOutlined, UndoOutlined } from '@ant-design/icons';
import { useState, useEffect } from "react";
import 'react-pdf/dist/Page/AnnotationLayer.css';
import 'react-pdf/dist/Page/TextLayer.css';

// Set up PDF.js worker
pdfjs.GlobalWorkerOptions.workerSrc = `//unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.mjs`;

const PdfPreviewModal = ({ previewPdfVisible, setPreviewPdfVisible, pdfPreviewData, onDocumentLoadSuccess }) => {
    const [zoomVal, setZoomVal] = useState(1.0);
    const [pdfError, setPdfError] = useState(null);
    const [, setIsLoading] = useState(true);
    const [numPages, setNumPages] = useState(0);

    // Reset state when pdfPreviewData changes (new PDF opened)
    useEffect(() => {
        if (pdfPreviewData) {
            setNumPages(0);  // Reset before new PDF loads
            setIsLoading(true);
            setPdfError(null);
        }
    }, [pdfPreviewData]);

    const handleDocumentLoadSuccess = (info) => {
        setIsLoading(false);
        setPdfError(null);
        setNumPages(info.numPages);  // Use internal state
        if (onDocumentLoadSuccess) {
            onDocumentLoadSuccess(info);
        }
    };

    const handleDocumentLoadError = (error) => {
        setIsLoading(false);
        setPdfError(error);
    };

    const handleRetry = () => {
        setPdfError(null);
        setIsLoading(true);
        setNumPages(0);  // Reset numPages on retry
    };

    const handleClose = () => {
        setPreviewPdfVisible(false);
        setZoomVal(1.0);
        setPdfError(null);
        setIsLoading(true);
        setNumPages(0);  // Reset numPages on close
    };

    // Zoom constraints
    const MIN_ZOOM = 0.5;
    const MAX_ZOOM = 3.0;

    // A plain URL is given when the file could not be read as a blob (it is
    // served cross-origin without CORS headers). react-pdf would hit the same
    // wall fetching it, so let the browser render it in an iframe instead.
    const isUrl = typeof pdfPreviewData === "string";

    return (
        <Modal
            onCancel={handleClose}
            title="PDF Preview"
            centered
            open={previewPdfVisible}
            footer={null}
            width={'80%'}
            style={{ maxWidth: 1200 }}
        >
            <Flex justify="center" style={{ display: isUrl ? "none" : undefined }}>
                <div style={{ marginTop: "10px", marginBottom: "10px" }}>
                    <ZoomOutOutlined
                        style={{
                            fontSize: "200%",
                            opacity: zoomVal <= MIN_ZOOM ? 0.3 : 1,
                            cursor: zoomVal <= MIN_ZOOM ? 'not-allowed' : 'pointer'
                        }}
                        className="cursor-pointer"
                        onClick={() => { if (zoomVal > MIN_ZOOM) setZoomVal(zoomVal - 0.25) }}
                    />
                    <UndoOutlined
                        style={{ fontSize: "200%", margin: "0 16px" }}
                        className="cursor-pointer"
                        onClick={() => { setZoomVal(1.0) }}
                    />
                    <ZoomInOutlined
                        style={{
                            fontSize: "200%",
                            opacity: zoomVal >= MAX_ZOOM ? 0.3 : 1,
                            cursor: zoomVal >= MAX_ZOOM ? 'not-allowed' : 'pointer'
                        }}
                        className="cursor-pointer"
                        onClick={() => { if (zoomVal < MAX_ZOOM) setZoomVal(zoomVal + 0.25) }}
                    />
                    <span style={{ marginLeft: 16, fontSize: 14 }}>
                        {Math.round(zoomVal * 100)}%
                    </span>
                </div>
            </Flex>
            <Flex
                justify="center"
                style={{ backgroundColor: "#999", minHeight: 400, maxHeight: '70vh', overflowY: 'auto' }}
            >
                {isUrl ? (
                    <iframe
                        title="PDF Preview"
                        src={pdfPreviewData}
                        style={{ width: "100%", height: "70vh", border: "none" }}
                    />
                ) : pdfError ? (
                    <Result
                        status="error"
                        title="Failed to Load PDF"
                        subTitle="There was an error loading this PDF file."
                        extra={[
                            <Button key="retry" type="primary" onClick={handleRetry}>
                                Try Again
                            </Button>,
                            <Button key="close" onClick={handleClose}>
                                Close
                            </Button>,
                        ]}
                    />
                ) : (
                    <div style={{ marginTop: "10px", marginBottom: "10px" }}>
                        <Document
                            file={pdfPreviewData}
                            onLoadSuccess={handleDocumentLoadSuccess}
                            onLoadError={handleDocumentLoadError}
                            loading={<Spin size="large" tip="Loading PDF..." />}
                            error={null}
                        >
                            {numPages > 0 && [...Array(numPages).keys()].map((item, index) => {
                                return (
                                    <div key={index + 1} style={{ marginTop: "10px" }}>
                                        <Page
                                            pageNumber={index + 1}
                                            scale={zoomVal}
                                            renderTextLayer={false}
                                            renderAnnotationLayer={false}
                                        />
                                    </div>
                                );
                            })}
                        </Document>
                    </div>
                )}
            </Flex>
        </Modal>
    )
}

export default PdfPreviewModal;
